import { forwardRef, ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { ConsumptionAndEmissionText } from '~/app/compositions/content/consumption-and-emission-text/consumption-emission-text';
import { Controls, Gallery } from '~/app/compositions/content/gallery/gallery';
import { groupMedia } from '~/app/compositions/content/gallery/gallery-utils';
import {
	ConfigurationStepStyle,
	SelectionStepSectionStyle,
} from '~/app/compositions/sidebar/trim-line/styles/selection-step-section-style';
import { Disclaimer } from '~/components/disclaimer/disclaimer';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { ExtendedCatalogImage } from '~/graphql/hooks/use-images';
import { useBreakPoint } from '~/hooks/responsive/useBreakPoint';
import { mobileBreakPoint } from '~/styles/break-points';
import { getHeaderId } from '~/utils/a11y-ids';

interface Props {
	id: string;
	media?: Array<Array<ExtendedCatalogImage | null | undefined>>;
	galleryType?: 'singleView' | 'onlySingleImg';
	children: ReactNode;
	galleryControls?: Controls;
	hide?: boolean;
	headline?: string | ReactNode;
	disclaimer?: string;
}

// Forward ref to the ConfigurationStep component
export const ConfigurationStep = forwardRef<HTMLDivElement, Props>(
	({ id, media, galleryControls, children, galleryType, hide, headline, disclaimer }, ref) => {
		const { isMobile, isMobileView } = useBreakPoint();
		const { isLoading } = useConfigurator();
		const { isCustomBreakpoint } = useBreakPoint(mobileBreakPoint);

		const _galleryType = isMobile || isMobileView ? 'mobile' : galleryType;
		// Trimline should have different aspect ratio when not on mobile and the gallery will always be single view
		const galleryAspectRatio = isMobile || isMobileView ? '4 / 3' : id === 'trimline' ? '15 / 9' : '18 / 9';

		return (
			<SelectionStepSectionStyle data-testid={`${id}${hide ? ' hidden' : ''}`} ref={ref} $hide={hide}>
				{/* On mobile we have to change the order: headline -> gallery -> configuration (shown as column, focus order should stay the same) */}
				{!isCustomBreakpoint && (
					<>
						<div id={'step-headline-container'} className="step-headline-container">
							{headline}
							{disclaimer && <Disclaimer type="category" content={disclaimer} />}
						</div>
						<div className={'gallery-wrapper'}>
							{!isLoading && media ? (
								<Gallery
									id={id}
									controls={galleryControls}
									media={media && media.length > 0 ? groupMedia(media, _galleryType) : []}
									galleryAspectRatio={galleryAspectRatio}
								/>
							) : (
								<Skeleton style={{ aspectRatio: galleryAspectRatio, width: '100%' }} />
							)}
							<ConsumptionAndEmissionText containerClass="paddings" />
						</div>
					</>
				)}
				<ConfigurationStepStyle>
					{isLoading ? (
						<div className="headline skeleton">
							<Skeleton height={20} width={250} />
							<Skeleton height={160} />
						</div>
					) : (
						<ContentContainer tabIndex={0} aria-labelledby={getHeaderId(id)}>
							{isCustomBreakpoint && (
								<div id={'step-headline-container'} className="step-headline-container-desktop">
									{headline}
									{disclaimer && <Disclaimer type="category" content={disclaimer} />}
								</div>
							)}
							{children}
						</ContentContainer>
					)}
				</ConfigurationStepStyle>
				{/* On desktop we have to display the step in a row, on the left side the gallery is displayed, on the right side the headline and configuration is shown as a column. The focus order should be: headline -> configuration -> gallery */}
				{isCustomBreakpoint && (
					<div className={'gallery-wrapper'}>
						{!isLoading && media ? (
							<Gallery
								id={id}
								controls={galleryControls}
								media={media && media.length > 0 ? groupMedia(media, _galleryType) : []}
								galleryAspectRatio={galleryAspectRatio}
							/>
						) : (
							<Skeleton style={{ aspectRatio: galleryAspectRatio, width: '100%' }} />
						)}
						<ConsumptionAndEmissionText containerClass="paddings" />
					</div>
				)}
			</SelectionStepSectionStyle>
		);
	},
);

ConfigurationStep.displayName = 'ConfigurationStep';

const ContentContainer = styled.div`
	display: flex;
	flex-direction: column;
`;
